import { RibbonId } from './ribbonId';
import { LabelPreferenceId } from '../store/schema/labelPreferencesIds';
import type { MailRibbonGroup } from '../store/schema/mailRibbonGroup';
import { MailRibbonConfigStore } from '..';

function getRibbonSectionContents(groupId: RibbonId, controlIds: RibbonId[]): MailRibbonGroup {
    return {
        groupId,
        groupName: undefined,
        controlIds,
    };
}

/**
 * This is the default configuration for OWA/Monarch's ribbon. Should there be no user
 * customization, this ribbon will be used as is.
 */
export const defaultConfig: MailRibbonConfigStore = {
    multiline: {
        homeTab: {
            layout: [
                getRibbonSectionContents(RibbonId.Group_ToggleLeftPane, [RibbonId.ToggleLeftPane]),
                getRibbonSectionContents(RibbonId.Group_New, [RibbonId.NewMessage]),
                getRibbonSectionContents(RibbonId.Group_Delete, [
                    RibbonId.IgnoreStopIgnore,
                    RibbonId.JunkFlyout,
                    RibbonId.DeleteItem,
                    RibbonId.Archive,
                ]),
                getRibbonSectionContents(RibbonId.Group_Respond, [
                    RibbonId.RespondReply,
                    RibbonId.RespondReplyAll,
                    RibbonId.RespondForward,
                    RibbonId.ReplyWithMeeting,
                ]),
                getRibbonSectionContents(RibbonId.Group_QuickSteps, [RibbonId.QuickStepsGallery]),
                getRibbonSectionContents(RibbonId.Group_Move, [
                    RibbonId.Sweep,
                    RibbonId.Move,
                    RibbonId.Rules,
                ]),
                getRibbonSectionContents(RibbonId.Group_Tags, [
                    RibbonId.ReadUnread,
                    RibbonId.Categorize,
                    RibbonId.FlagUnflag,
                    RibbonId.PinUnpin,
                    RibbonId.Snooze,
                    RibbonId.AssignPolicy,
                ]),
                getRibbonSectionContents(RibbonId.Group_Find, [
                    RibbonId.AddressBook,
                    RibbonId.BrowseGroups,
                ]),
                getRibbonSectionContents(RibbonId.Group_Speech, [RibbonId.ReadAloud]),
                getRibbonSectionContents(RibbonId.Group_Undo, [RibbonId.Undo]),
                getRibbonSectionContents(RibbonId.Group_AddIns, [RibbonId.GetAddIns]),
                getRibbonSectionContents(RibbonId.Group_Insights, [RibbonId.Insights]),
                getRibbonSectionContents(RibbonId.Group_Protection, [RibbonId.ReportMessage]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                RibbonId.Group_ToggleLeftPane,
                RibbonId.Group_New,
                RibbonId.Group_Delete,
                RibbonId.Group_Respond,
                RibbonId.Group_QuickSteps,
                RibbonId.Group_Move,
                RibbonId.Group_Tags,
                RibbonId.Group_Find,
                RibbonId.Group_Speech,
                RibbonId.Group_Undo,
                RibbonId.Group_AddIns,
                RibbonId.Group_Insights,
                RibbonId.Group_Protection,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        viewTab: {
            layout: [
                getRibbonSectionContents(RibbonId.Group_ImmersiveReader, [
                    RibbonId.ImmersiveReader,
                ]),
                getRibbonSectionContents(RibbonId.Group_Print, [RibbonId.Print]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [RibbonId.Group_ImmersiveReader, RibbonId.Group_Print],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        formatTab: {
            layout: [],
            controlsInOverflow: [],
            staticGroupIdOrdering: [],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        messageTab: {
            layout: [
                getRibbonSectionContents(RibbonId.Group_Clipboard, [
                    RibbonId.Paste,
                    RibbonId.FormatPainter,
                ]),
                getRibbonSectionContents(RibbonId.Group_Names, [
                    RibbonId.AddressBook,
                    RibbonId.CheckNames,
                ]),
                getRibbonSectionContents(RibbonId.Group_Include, [
                    RibbonId.AttachFile,
                    RibbonId.AttachSig,
                ]),
                getRibbonSectionContents(RibbonId.Group_Sensitivity, [RibbonId.Sensitivity]),
                getRibbonSectionContents(RibbonId.Group_Tags, [
                    RibbonId.HighImportance,
                    RibbonId.LowImportance,
                    RibbonId.FlagUnflag,
                    RibbonId.AssignPolicy,
                ]),
                getRibbonSectionContents(RibbonId.Group_Voice, [RibbonId.Dictate]),
                getRibbonSectionContents(RibbonId.Group_ImmersiveReader, [
                    RibbonId.ImmersiveReader,
                ]),
                getRibbonSectionContents(RibbonId.Group_Speech, [RibbonId.ReadAloud]),
                getRibbonSectionContents(RibbonId.Group_Encrypt, [RibbonId.Encrypt]),
                getRibbonSectionContents(RibbonId.Group_Save, [RibbonId.Save]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                RibbonId.Group_Clipboard,
                RibbonId.Group_Names,
                RibbonId.Group_Include,
                RibbonId.Group_Sensitivity,
                RibbonId.Group_Tags,
                RibbonId.Group_Voice,
                RibbonId.Group_ImmersiveReader,
                RibbonId.Group_Speech,
                RibbonId.Group_Encrypt,
                RibbonId.Group_Save,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
    },
    singleline: {
        homeTab: {
            layout: [
                getRibbonSectionContents(RibbonId.Group_Move_and_Delete, [
                    RibbonId.ToggleLeftPane,
                    RibbonId.NewMessage,
                    RibbonId.DeleteItem,
                    RibbonId.Archive,
                    RibbonId.Sweep,
                    RibbonId.Move,
                    RibbonId.JunkFlyout,
                    RibbonId.Rules,
                ]),
                getRibbonSectionContents(RibbonId.Group_Respond, [
                    RibbonId.RespondReply,
                    RibbonId.RespondReplyAll,
                    RibbonId.RespondForward,
                    RibbonId.ReplyWithMeeting,
                ]),
                getRibbonSectionContents(RibbonId.Group_Tags, [
                    RibbonId.ReadUnread,
                    RibbonId.Categorize,
                    RibbonId.FlagUnflag,
                    RibbonId.PinUnpin,
                    RibbonId.Snooze,
                    RibbonId.AssignPolicy,
                ]),
                getRibbonSectionContents(RibbonId.Group_Find, [
                    RibbonId.AddressBook,
                    RibbonId.BrowseGroups,
                ]),
                getRibbonSectionContents(RibbonId.Group_Undo, [RibbonId.Undo]),
                getRibbonSectionContents(RibbonId.Group_AddIns, [RibbonId.GetAddIns]),
                getRibbonSectionContents(RibbonId.Group_RibbonCustomizer, [
                    RibbonId.RibbonCustomizer,
                ]),
            ],
            controlsInOverflow: [
                RibbonId.BrowseGroups,
                RibbonId.JunkFlyout,
                RibbonId.ReplyWithMeeting,
                RibbonId.Rules,
            ],
            staticGroupIdOrdering: [
                RibbonId.Group_New,
                RibbonId.Group_Respond,
                RibbonId.Group_Tags,
                RibbonId.Group_Undo,
                RibbonId.Group_Find,
                RibbonId.Group_AddIns,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        viewTab: {
            layout: [
                getRibbonSectionContents(RibbonId.Group_ImmersiveReader, [
                    RibbonId.ImmersiveReader,
                ]),
                getRibbonSectionContents(RibbonId.Group_Print, [RibbonId.Print]),
                getRibbonSectionContents(RibbonId.Group_RibbonCustomizer, [
                    RibbonId.RibbonCustomizer,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                RibbonId.Group_ImmersiveReader,
                RibbonId.Group_Print,
                RibbonId.Group_RibbonCustomizer,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        formatTab: {
            layout: [],
            controlsInOverflow: [],
            staticGroupIdOrdering: [],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        messageTab: {
            layout: [
                getRibbonSectionContents(RibbonId.Group_Clipboard, [
                    RibbonId.Paste,
                    RibbonId.FormatPainter,
                ]),
                getRibbonSectionContents(RibbonId.Group_Names, [
                    RibbonId.AddressBook,
                    RibbonId.CheckNames,
                ]),
                getRibbonSectionContents(RibbonId.Group_Include, [
                    RibbonId.AttachFile,
                    RibbonId.AttachSig,
                ]),
                getRibbonSectionContents(RibbonId.Group_Sensitivity, [RibbonId.Sensitivity]),
                getRibbonSectionContents(RibbonId.Group_Tags, [
                    RibbonId.HighImportance,
                    RibbonId.LowImportance,
                    RibbonId.FlagUnflag,
                    RibbonId.AssignPolicy,
                ]),
                getRibbonSectionContents(RibbonId.Group_Voice, [RibbonId.Dictate]),
                getRibbonSectionContents(RibbonId.Group_ImmersiveReader, [
                    RibbonId.ImmersiveReader,
                ]),
                getRibbonSectionContents(RibbonId.Group_Speech, [RibbonId.ReadAloud]),
                getRibbonSectionContents(RibbonId.Group_Encrypt, [RibbonId.Encrypt]),
                getRibbonSectionContents(RibbonId.Group_Save, [RibbonId.Save]),
                getRibbonSectionContents(RibbonId.Group_RibbonCustomizer, [
                    RibbonId.RibbonCustomizer,
                ]),
            ],
            controlsInOverflow: [RibbonId.Save],
            staticGroupIdOrdering: [
                RibbonId.Group_Clipboard,
                RibbonId.Group_Names,
                RibbonId.Group_Include,
                RibbonId.Group_Sensitivity,
                RibbonId.Group_Tags,
                RibbonId.Group_Voice,
                RibbonId.Group_ImmersiveReader,
                RibbonId.Group_Speech,
                RibbonId.Group_Encrypt,
                RibbonId.Group_Save,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
    },
    toolbar: {
        homeTab: {
            layout: [
                getRibbonSectionContents(RibbonId.Group_Toolbar_Home, [
                    RibbonId.ToggleLeftPane,
                    RibbonId.NewMessage,
                    RibbonId.DeleteItem,
                    RibbonId.Archive,
                    RibbonId.JunkFlyout,
                    RibbonId.Sweep,
                    RibbonId.Move,
                    RibbonId.Categorize,
                    RibbonId.Snooze,
                    RibbonId.Undo,
                    RibbonId.ReadUnread,
                    RibbonId.FlagUnflag,
                    RibbonId.PinUnpin,
                    RibbonId.IgnoreStopIgnore,
                    RibbonId.CreateRule,
                    RibbonId.Print,
                    RibbonId.ImmersiveReader,
                    RibbonId.AssignPolicy,
                    RibbonId.RibbonCustomizer,
                ]),
            ],
            controlsInOverflow: [
                RibbonId.AssignPolicy,
                RibbonId.CreateRule,
                RibbonId.FlagUnflag,
                RibbonId.IgnoreStopIgnore,
                RibbonId.ImmersiveReader,
                RibbonId.PinUnpin,
                RibbonId.Print,
                RibbonId.ReadUnread,
                RibbonId.RibbonCustomizer,
            ],
            staticGroupIdOrdering: [RibbonId.Group_Toolbar_Home],
            showLabelsPreference: LabelPreferenceId.AlwaysShow,
            removedDefaultGroups: [],
        },
        messageTab: {
            layout: [
                getRibbonSectionContents(RibbonId.Group_Toolbar_Message, [
                    RibbonId.ToggleLeftPane,
                    RibbonId.NewMessage,
                    RibbonId.SendDraft,
                    RibbonId.AttachFile,
                    RibbonId.Sensitivity,
                    RibbonId.DiscardDraft,
                    RibbonId.Save,
                    RibbonId.AttachSig,
                    RibbonId.ShowHideFrom,
                    RibbonId.Encrypt,
                    RibbonId.SetImportance,
                    RibbonId.ShowMessageOptions,
                    RibbonId.SwitchDraftFormat,
                    RibbonId.CheckForAccessibilityIssues,
                    RibbonId.RibbonCustomizer,
                ]),
            ],
            controlsInOverflow: [
                RibbonId.AttachSig,
                RibbonId.CheckForAccessibilityIssues,
                RibbonId.Encrypt,
                RibbonId.SetImportance,
                RibbonId.RibbonCustomizer,
                RibbonId.Save,
                RibbonId.ShowHideFrom,
                RibbonId.ShowMessageOptions,
                RibbonId.SwitchDraftFormat,
            ],
            staticGroupIdOrdering: [RibbonId.Group_Toolbar_Message],
            showLabelsPreference: LabelPreferenceId.AlwaysShow,
            removedDefaultGroups: [],
        },
    },
    selectedTab: RibbonId.Tab_Home,
};
